/* eslint-disable comma-dangle */
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { RouteRecordRaw } from 'vue-router';
import app from '@/main';
import { getCurrentClientInfinityAddonConfig } from '@/shared/helpers/infinity-addon-config';
import { getRouteMetaTitle } from '@/shared/helpers/route-meta-translations';
import { ERouteMetaPermissionMode, IRouteConfigAuthMeta, IRouteMetaPermissionConfig } from '@/shared';

const leadsReportingAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ key: 'leads_reporting', keyType: ModelType.building, mode: ERouteMetaPermissionMode.AT_LEAST_ONE }],
};

const websiteAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ key: 'websites_reporting', keyType: ModelType.site, mode: ERouteMetaPermissionMode.AT_LEAST_ONE }],
};

const floorplanNavigatorAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ key: 'floorplan_navigator_reporting', keyType: ModelType.building, mode: ERouteMetaPermissionMode.AT_LEAST_ONE }],
};

const websiteCallTrackingAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [
    ...websiteAuth.permissions as IRouteMetaPermissionConfig[],
    ...leadsReportingAuth.permissions as IRouteMetaPermissionConfig[],
  ],
};

const premiumReportingAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ key: 'premium_reporting', keyType: ModelType.account }],
};

// NOTE: use the permission-config form (with `checkAtLeastOneModel`), not a plain
// string[]. With `condition: 'or'` the guard evaluates string[] permissions via
// `hasAtleastOneProductBasedOnStringArray`, which uses `models.every(...)` and so
// returns TRUE vacuously when the client has no products in a model type — letting
// clients with none of these reporting perms through. The config form uses
// `models.some(...)` and gates correctly.
const marketInsightsTeaserAuth: IRouteConfigAuthMeta = {
  required: true,
  condition: 'or',
  permissions: [
    { key: 'leads_reporting', keyType: ModelType.building, checkAtLeastOneModel: true },
    { key: 'websites_reporting', keyType: ModelType.site, checkAtLeastOneModel: true },
    { key: 'tours_reporting', keyType: ModelType.building, checkAtLeastOneModel: true },
    { key: 'custom_reporting', keyType: ModelType.account, checkAtLeastOneModel: true },
    { key: 'floorplan_navigator_reporting', keyType: ModelType.building, checkAtLeastOneModel: true },
    { key: 'digital_marketing_reporting', keyType: ModelType.building, checkAtLeastOneModel: true },
    { key: 'listing_scores_reporting', keyType: ModelType.building, checkAtLeastOneModel: true },
    { key: 'premium_reporting', keyType: ModelType.account, checkAtLeastOneModel: true },
  ],
};

const toursReportingAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ key: 'tours_reporting', keyType: ModelType.building, mode: ERouteMetaPermissionMode.AT_LEAST_ONE }],
};

const customReporting: RouteRecordRaw = {
  path: '/reporting/market-insights/custom-reporting',
  name: 'CustomReporting',
  component: () => import('@/views/reporting/CustomReporting.vue'),
  meta: {
    // Custom Reporting lives under Market Insights and is available to any client
    // with reporting access — same gate as the Market Insights nav itself.
    auth: marketInsightsTeaserAuth,
    title: 'Custom Reporting',
    titleKey: 'reporting.views.customReporting.routeTitle',
    icon: 'chart-bar',
    active: false,
    hidePageTitle: false,
    noPagePadding: false,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: false,
    },
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ]
  },
};

// Preserve old bookmarks/links after Custom Reporting moved under Market Insights.
const customReportingLegacyRedirect: RouteRecordRaw = {
  path: '/reporting/custom-reporting',
  redirect: { name: 'CustomReporting' },
};

/**
 * NEW ROUTING
 *
 *
 *
 */
const reportingTotalLeadsOverview: RouteRecordRaw = {
  path: '/reporting/leads/total/overview',
  name: 'ReportingTotalLeadsOverview',
  component: () => import('@/views/reporting/sisense/ReportLeadOverview.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Leads Overview',
    titleKey: 'reporting.views.totalLeadsOverview.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.views.totalLeadsOverview.breadcrumbLeaf') ?? 'Overview',
      },
    ],
  },
};

const reportingTotalLeadsByDay: RouteRecordRaw = {
  path: '/reporting/leads/total/day/overview',
  name: 'ReportingTotalLeadsByDay',
  component: () => import('@/views/reporting/sisense/ReportLeadDay.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Total Leads By Day',
    titleKey: 'reporting.views.totalLeadsByDay.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/day/overview',
        title: vm?.$ts('reporting.views.totalLeadsByDay.breadcrumbLeaf') ?? 'By Day',
      },
    ],
  },
};

const reportingTotalLeadsByDayOfWeek: RouteRecordRaw = {
  path: '/reporting/leads/total/day/of-week',
  name: 'ReportingTotalLeadsByDayOfWeek',
  component: () => import('@/views/reporting/sisense/ReportLeadDayOfWeek.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Total Leads by Day of Week',
    titleKey: 'reporting.views.totalLeadsByDayOfWeek.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/day/of-week',
        title: vm?.$ts('reporting.views.totalLeadsByDayOfWeek.breadcrumbLeaf') ?? 'By Day of Week',
      },
    ],
  },
};

const reportingTotalLeadsByHourOfDay: RouteRecordRaw = {
  path: '/reporting/leads/total/day/hour-of-day',
  name: 'ReportingTotalLeadsByHourOfDay',
  component: () => import('@/views/reporting/sisense/ReportLeadHourOfDay.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Total Leads by Hour of Day',
    titleKey: 'reporting.views.totalLeadsByHourOfDay.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/day/hour-of-day',
        title: vm?.$ts('reporting.views.totalLeadsByHourOfDay.breadcrumbLeaf') ?? 'By Hour of Day',
      },
    ],
  },
};

const reportingTotalLeadsAvgLeadProperty: RouteRecordRaw = {
  path: '/reporting/leads/total/day/avg-lead-property',
  name: 'ReportingTotalLeadsAvgLeadPropert',
  component: () => import('@/views/reporting/sisense/ReportLeadAvgLeadProp.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Average Leads per Active Property per Day',
    titleKey: 'reporting.views.totalLeadsAvgLeadProperty.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/day/avg-lead-property',
        title: vm?.$ts('reporting.views.totalLeadsAvgLeadProperty.breadcrumbLeaf') ?? 'Average Per Property Per Day',
      },
    ],
  },
};

const reportingTotalLeadsByProperty: RouteRecordRaw = {
  path: '/reporting/leads/total/property/overview',
  name: 'ReportingTotalLeadsByProperty',
  component: () => import('@/views/reporting/sisense/ReportLeadProperty.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Total Leads by Property',
    titleKey: 'reporting.views.totalLeadsByProperty.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/property/overview',
        title: vm?.$ts('reporting.views.totalLeadsByProperty.breadcrumbLeaf') ?? 'By Property',
      },
    ],
  },
};

const reportingTotalLeadsByPropertyType: RouteRecordRaw = {
  path: '/reporting/leads/total/property/type',
  name: 'ReportingTotalLeadsByPropertyType',
  component: () => import('@/views/reporting/sisense/ReportLeadPropertyType.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Total Leads by Property Type',
    titleKey: 'reporting.views.totalLeadsByPropertyType.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/property/type',
        title: vm?.$ts('reporting.views.totalLeadsByPropertyType.breadcrumbLeaf') ?? 'By Property Type',
      },
    ],
  },
};

const reportingTotalLeadsByCity: RouteRecordRaw = {
  path: '/reporting/leads/total/city/overview',
  name: 'ReportingTotalLeadsByCity',
  component: () => import('@/views/reporting/sisense/ReportLeadCity.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Total Leads by City',
    titleKey: 'reporting.views.totalLeadsByCity.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/city/overview',
        title: vm?.$ts('reporting.views.totalLeadsByCity.breadcrumbLeaf') ?? 'By City',
      },
    ],
  },
};

const reportingTotalLeadsByCityPerProp: RouteRecordRaw = {
  path: '/reporting/leads/total/city/property',
  name: 'ReportingTotalLeadsByCityPerProp',
  component: () => import('@/views/reporting/sisense/ReportLeadCityPerProperty.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Average Leads Per Property By City',
    titleKey: 'reporting.views.totalLeadsByCityPerProperty.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/city/property',
        title: vm?.$ts('reporting.views.totalLeadsByCityPerProperty.breadcrumbLeaf') ?? 'By City Per Property',
      },
    ],
  },
};

const reportingTotalLeadsBySourceProperty: RouteRecordRaw = {
  path: '/reporting/leads/total/source/property',
  name: 'ReportingTotalLeadsBySourceProperty',
  component: () => import('@/views/reporting/sisense/ReportLeadSourceProperty.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Average Leads per Active Property by Source',
    titleKey: 'reporting.views.totalLeadsBySourceProperty.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/source/property',
        title: vm?.$ts('reporting.views.totalLeadsBySourceProperty.breadcrumbLeaf') ?? 'Per Property By Source',
      },
    ],
  },
};

const reportingTotalLeadsBySourceDay: RouteRecordRaw = {
  path: '/reporting/leads/total/source/day',
  name: 'ReportingTotalLeadsBySourceDay',
  component: () => import('@/views/reporting/sisense/ReportLeadSourceDay.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Per Property by Source by Day',
    titleKey: 'reporting.views.totalLeadsBySourceDay.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.totalLeads') ?? 'Total Leads',
      },
      {
        to: '/reporting/leads/total/source/day',
        title: vm?.$ts('reporting.views.totalLeadsBySourceDay.breadcrumbLeaf') ?? 'Per Property by Source by Day',
      },
    ],
  },
};

const reportingSiteLeadsBySource: RouteRecordRaw = {
  path: '/reporting/leads/site/source',
  name: 'ReportingSiteLeadsBySource',
  component: () => import('@/views/reporting/sisense/ReportLeadSiteBySource.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'By Source',
    titleKey: 'reporting.views.siteLeadsBySource.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/site/overview',
        title: vm?.$ts('reporting.breadcrumbs.listingSiteLeads') ?? 'Listing Site Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingSiteLeadsOverview: RouteRecordRaw = {
  path: '/reporting/leads/site/overview',
  name: 'ReportingSiteLeadsOverview',
  component: () => import('@/views/reporting/sisense/ReportLeadSiteOverview.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Overview',
    titleKey: 'reporting.views.siteLeadsOverview.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/site/overview',
        title: vm?.$ts('reporting.breadcrumbs.listingSiteLeads') ?? 'Listing Site Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingSiteLeadsAdTrend: RouteRecordRaw = {
  path: '/reporting/leads/site/ad-trend',
  name: 'ReportingSiteLeadsAdTrend',
  component: () => import('@/views/reporting/sisense/ReportLeadSiteAdTrend.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Recent Trends',
    titleKey: 'reporting.views.siteLeadsAdTrend.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/site/overview',
        title: vm?.$ts('reporting.breadcrumbs.listingSiteLeads') ?? 'Listing Site Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingSiteLeadsInfinity: RouteRecordRaw = {
  path: '/reporting/leads/site/infinity',
  name: 'ReportingSiteLeadsInfinity',
  component: () => import('@/views/reporting/sisense/ReportLeadSiteLeadsInfinity.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Infinity',
    titleKey: 'reporting.views.siteLeadsInfinity.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/site/overview',
        title: vm?.$ts('reporting.breadcrumbs.listingSiteLeads') ?? 'Listing Site Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingLeadsWebsiteOverview: RouteRecordRaw = {
  path: '/reporting/leads/website/overview',
  name: 'ReportingLeadsWebsiteOverview',
  component: () => import('@/views/reporting/sisense/ReportLeadWebsiteOverview.vue'),
  meta: {
    auth: websiteAuth,
    title: 'Overview',
    titleKey: 'reporting.views.leadsWebsiteOverview.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/website/overview',
        title: vm?.$ts('reporting.breadcrumbs.websiteLeads') ?? 'Website Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingLeadsWebsiteReferral: RouteRecordRaw = {
  path: '/reporting/leads/website/referral',
  name: 'ReportingLeadsWebsiteReferral',
  component: () => import('@/views/reporting/sisense/ReportLeadWebsiteReferral.vue'),
  meta: {
    auth: websiteAuth,
    title: 'Referral Sources',
    titleKey: 'reporting.views.leadsWebsiteReferral.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/website/overview',
        title: vm?.$ts('reporting.breadcrumbs.websiteLeads') ?? 'Website Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingLeadsWebsiteDevices: RouteRecordRaw = {
  path: '/reporting/websites/devices',
  name: 'ReportingLeadsWebsiteDevices',
  component: () => import('@/views/reporting/sisense/ReportLeadWebsiteBreakdown.vue'),
  meta: {
    auth: websiteAuth,
    title: 'Website Devices',
    titleKey: 'reporting.views.leadsWebsiteDevices.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/websites/leads',
        title: vm?.$ts('reporting.breadcrumbs.website') ?? 'Website',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingFloorplanNavigator: RouteRecordRaw = {
  path: '/reporting/floorplan-navigator',
  name: 'ReportingFloorplanNavigator',
  component: () => import('@/views/reporting/sisense/ReportFloorplanNavigator.vue'),
  meta: {
    auth: floorplanNavigatorAuth,
    title: 'Floorplan Navigator',
    titleKey: 'reporting.views.floorplanNavigator.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingLeadsWebsiteCallReport: RouteRecordRaw = {
  path: '/reporting/leads/website/call-report',
  name: 'ReportingLeadsWebsiteCallReport',
  component: () => import('@/views/reporting/sisense/ReportLeadWebsiteCallReport.vue'),
  meta: {
    auth: websiteCallTrackingAuth,
    title: 'Call Report',
    titleKey: 'reporting.views.leadsWebsiteCallReport.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.leads') ?? 'Leads',
      },
      {
        to: '/reporting/leads/website/overview',
        title: vm?.$ts('reporting.breadcrumbs.websiteLeads') ?? 'Website Leads',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingCallTrackingDashboardOverview: RouteRecordRaw = {
  path: '/reporting/call-tracking/overview',
  name: 'ReportingCallTrackingOverview',
  component: () => import('@/views/reporting/sisense/ReportCallTrackingDashboardOverview.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Overview',
    titleKey: 'reporting.views.callTrackingOverview.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/call-tracking/overview',
        title: vm?.$ts('reporting.breadcrumbs.callTracking') ?? 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingCallTrackingCallsbySoruce: RouteRecordRaw = {
  path: '/reporting/call-tracking/calls-by-source',
  name: 'ReportingCallTrackingCallsBySource',
  component: () => import('@/views/reporting/sisense/ReportCallTrackingCallsBySource.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Calls By Source',
    titleKey: 'reporting.views.callTrackingCallsBySource.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/call-tracking/summary/overview',
        title: vm?.$ts('reporting.breadcrumbs.callTracking') ?? 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingCallTrackingCallsbyProperty: RouteRecordRaw = {
  path: '/reporting/call-tracking/calls-by-property',
  name: 'ReportingCallTrackingCallsByProperty',
  component: () => import('@/views/reporting/sisense/ReportCallTrackingCallsByProperty.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Calls By Property',
    titleKey: 'reporting.views.callTrackingCallsByProperty.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/call-tracking/overview',
        title: vm?.$ts('reporting.breadcrumbs.callTracking') ?? 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingCallTrackingDuration: RouteRecordRaw = {
  path: '/reporting/call-tracking/duration',
  name: 'ReportingCallTrackingDuration',
  component: () => import('@/views/reporting/sisense/ReportCallTrackingDuration.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Call Duration',
    titleKey: 'reporting.views.callTrackingDuration.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/call-tracking/overview',
        title: vm?.$ts('reporting.breadcrumbs.callTracking') ?? 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingCallTrackingUsage: RouteRecordRaw = {
  path: '/reporting/call-tracking/usage',
  name: 'ReportingCallTrackingUsage',
  component: () => import('@/views/reporting/sisense/ReportCallTrackingUsage.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Peak Usage',
    titleKey: 'reporting.views.callTrackingUsage.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/call-tracking/overview',
        title: vm?.$ts('reporting.breadcrumbs.callTracking') ?? 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingTours: RouteRecordRaw = {
  path: '/reporting/tours',
  name: 'ReportingTours',
  component: () => import('@/views/reporting/sisense/ReportTours.vue'),
  meta: {
    auth: toursReportingAuth,
    title: 'Tours',
    titleKey: 'reporting.views.tours.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingWebsitesLeads: RouteRecordRaw = {
  path: '/reporting/websites/leads',
  name: 'ReportingWebsitesLeads',
  component: () => import('@/views/reporting/sisense/ReportWebsitesLeads.vue'),
  meta: {
    auth: websiteAuth,
    title: 'Overview',
    titleKey: 'reporting.views.websitesLeads.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/websites/leads',
        title: vm?.$ts('reporting.breadcrumbs.website') ?? 'Website',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingWebsitesTraffic: RouteRecordRaw = {
  path: '/reporting/websites/traffic',
  name: 'ReportingWebsitesTraffic',
  component: () => import('@/views/reporting/sisense/ReportWebsitesTraffic.vue'),
  meta: {
    auth: websiteAuth,
    title: 'Website Traffic',
    titleKey: 'reporting.views.websitesTraffic.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/websites/leads',
        title: vm?.$ts('reporting.breadcrumbs.website') ?? 'Website',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingWebsitesEvents: RouteRecordRaw = {
  path: '/reporting/websites/events',
  name: 'ReportingWebsitesEvents',
  component: () => import('@/views/reporting/sisense/ReportWebsitesEvents.vue'),
  meta: {
    auth: websiteAuth,
    title: 'Website Events',
    titleKey: 'reporting.views.websitesEvents.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/websites/leads',
        title: vm?.$ts('reporting.breadcrumbs.website') ?? 'Website',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingGoogleAds: RouteRecordRaw = {
  path: '/reporting/marketing/google-ads',
  name: 'ReportingGoogleAds',
  component: () => import('@/views/reporting/sisense/ReportMarkettingGoogleAds.vue'),
  meta: {
    auth: {
      required: true,
      permissions: [{ key: 'digital_marketing_reporting', keyType: ModelType.building, mode: ERouteMetaPermissionMode.AT_LEAST_ONE }]
    },
    title: 'Google Ads',
    titleKey: 'reporting.views.googleAds.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: '/reporting/marketing/google-ads',
        title: vm?.$ts('reporting.breadcrumbs.marketing') ?? 'Marketing',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingListingScores: RouteRecordRaw = {
  path: '/reporting/listing-scores',
  name: 'ReportingListingScores',
  component: () => import('@/views/reporting/sisense/ReportListingScores.vue'),
  meta: {
    auth: {
      required: true,
      permissions: [{ key: 'listing_scores_reporting', keyType: ModelType.building, mode: ERouteMetaPermissionMode.AT_LEAST_ONE }]
    },
    isDynamicPermitted: () => app()?.$rsGlobals.$isListingScoresDisabled,
    title: 'Listing Scores',
    titleKey: 'reporting.views.listingScores.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

const reportingPremiumMarketData: RouteRecordRaw = {
  path: '/reporting/market-insights/market-data',
  name: 'ReportingMarketData',
  component: () => import('@/views/reporting/sisense/ReportPremiumMarketData.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Market Data',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const reportingPremiumHistoricalMarketData: RouteRecordRaw = {
  path: '/reporting/market-insights/historical-market-data',
  name: 'ReportingHistoricalMarketData',
  component: () => import('@/views/reporting/sisense/ReportPremiumHistoricalMarketData.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Historical Market Data',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const reportingPremiumMarketDataTeaser: RouteRecordRaw = {
  path: '/reporting/market-insights/market-data-teaser',
  name: 'ReportingMarketDataTeaser',
  component: () => import('@/views/reporting/sisense/ReportPremiumMarketDataTeaser.vue'),
  meta: {
    auth: marketInsightsTeaserAuth,
    title: 'Market Data',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const reportingPremiumPortfolioComparison: RouteRecordRaw = {
  path: '/reporting/market-insights/portfolio-comparison',
  name: 'ReportingPortfolioComparison',
  component: () => import('@/views/reporting/sisense/ReportPremiumPortfolioComparison.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Portfolio Comparison',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const canAccessInfinityPLPortfolioComparison = () => {
  const vm = app();
  if (!vm) return false;

  // Infinity PL comparison is only for clients without premium reporting access.
  if (vm.$rsGlobals.$userHasAtLeastOneAccess('premium_reporting')) {
    return false;
  }

  const config = getCurrentClientInfinityAddonConfig();
  return config?.marketInsights !== null && config?.marketInsights !== undefined;
};

const reportingInfinityPLPortfolioComparison: RouteRecordRaw = {
  path: '/reporting/market-insights/infinity-portfolio-comparison',
  name: 'ReportingInfinityPLPortfolioComparison',
  component: () => import('@/views/reporting/sisense/ReportInfinityPLPortfolioComparison.vue'),
  meta: {
    auth: {
      required: true,
      isDynamicPermitted: canAccessInfinityPLPortfolioComparison,
    },
    title: 'Portfolio Comparison',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    dynamicHideFromNav: () => !canAccessInfinityPLPortfolioComparison(),
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const reportingPremiumMarketComparisonCity: RouteRecordRaw = {
  path: '/reporting/market-insights/market-comparison-city',
  name: 'ReportingMarketComparisonCity',
  component: () => import('@/views/reporting/sisense/ReportPremiumMarketComparisonCity.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Market Comparison (City)',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const reportingPremiumMarketComparisonNeighbourhood: RouteRecordRaw = {
  path: '/reporting/market-insights/market-comparison-neighbourhood',
  name: 'ReportingMarketComparisonNeighbourhood',
  component: () => import('@/views/reporting/sisense/ReportPremiumMarketComparisonNeighbourhood.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Market Comparison (Neighbourhood)',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const reportingPremiumDefaultSettings: RouteRecordRaw = {
  path: '/reporting/market-insights/settings/default',
  name: 'ReportingPremiumSettingsDefault',
  component: () => import('@/views/reporting/sisense/ReportPremiumDefaultSettings.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Default Setting',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
    footerBar: {
      hasTranslations: false,
      enabled: true,
      hasSave: true,
    },
  },
};

const reportingPremiumCitiesList: RouteRecordRaw = {
  path: '/reporting/market-insights/settings/cities-list',
  name: 'ReportingPremiumSettingsCitiesList',
  component: () => import('@/views/reporting/sisense/ReportPremiumCitiesList.vue'),
  meta: {
    auth: premiumReportingAuth,
    title: 'Cities List',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/market-insights',
        title: 'Market Insights',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
    footerBar: {
      hasTranslations: false,
      enabled: true,
      hasSave: true,
    },
  },
};

const reportingListingEngagements: RouteRecordRaw = {
  path: '/reporting/listings-engagement',
  name: 'ReportingListingEngagements',
  component: () => import('@/views/reporting/sisense/ReportingListingEngagements.vue'),
  meta: {
    auth: leadsReportingAuth,
    title: 'Listings Engagement',
    titleKey: 'reporting.views.listingEngagements.routeTitle',
    icon: 'chart-bar',
    hidePageTitle: true,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/reporting/leads/total/overview',
        title: vm?.$ts('reporting.breadcrumbs.root') ?? 'Reporting',
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm.$route),
      },
    ],
  },
};

/**
 * Reporting route relation mapping
 */

export default [
  reportingTotalLeadsOverview,
  reportingTotalLeadsByDay,
  reportingTotalLeadsByDayOfWeek,
  reportingTotalLeadsByHourOfDay,
  reportingTotalLeadsAvgLeadProperty,
  reportingTotalLeadsByPropertyType,
  reportingTotalLeadsByCityPerProp,
  reportingTotalLeadsByProperty,
  reportingTotalLeadsByCity,
  reportingTotalLeadsBySourceProperty,
  reportingTotalLeadsBySourceDay,
  reportingSiteLeadsBySource,
  reportingSiteLeadsOverview,
  reportingSiteLeadsAdTrend,
  reportingSiteLeadsInfinity,
  reportingLeadsWebsiteOverview,
  reportingLeadsWebsiteReferral,
  reportingLeadsWebsiteDevices,
  reportingFloorplanNavigator,
  reportingLeadsWebsiteCallReport,
  reportingCallTrackingDashboardOverview,
  reportingCallTrackingCallsbySoruce,
  reportingCallTrackingCallsbyProperty,
  reportingCallTrackingDuration,
  reportingCallTrackingUsage,
  reportingTours,
  reportingWebsitesLeads,
  reportingWebsitesTraffic,
  reportingWebsitesEvents,
  reportingGoogleAds,
  reportingListingScores,
  customReporting,
  customReportingLegacyRedirect,
  reportingPremiumMarketData,
  reportingPremiumHistoricalMarketData,
  reportingPremiumMarketDataTeaser,
  reportingPremiumPortfolioComparison,
  reportingInfinityPLPortfolioComparison,
  reportingPremiumMarketComparisonCity,
  reportingPremiumMarketComparisonNeighbourhood,
  reportingPremiumDefaultSettings,
  reportingPremiumCitiesList,
  reportingListingEngagements,
];
